import React, {useEffect, useMemo, useState} from "react";
import {connect} from "react-redux";
import {bindActionCreators} from "redux";
import UNINITIALISED from "utils/uninitialised";
import requestor from "requestor";
import workflowsFetchAction from "modules/flowmaster/actions/workflows_fetch";
import workflowsClearAction from "modules/flowmaster/actions/workflows_clear";

import {IssueId, Issue} from "common/types/issue";
import {OrganisationId} from "common/types/organisation";
import {Workflow} from "common/flowmaster/types/workflow";
import {Definition} from "common/flowmaster/types/data_asset";
import DefinitionSelector from "common_components/flowmaster/data_asset/definition_selector";
import ConfigurationSelector from "common_components/flowmaster/data_asset/configuration_selector";
import {Link} from "react-router";
import {IconButton, Paper} from "@material-ui/core";
import LinkIcon from "@material-ui/icons/Link";

const styles = {
  formControl: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: "auto",
    width: "auto",
  },
};

interface DataAssetEditorProps {
  workflows: Definition[];
  getWorkflows: (
    organisationId: OrganisationId,
    params: {fetch_data_assets: boolean},
  ) => void;
  clearWorkflows: () => void;
  issueId: IssueId;
  organisationId: OrganisationId;
  llm_settings: Issue["llm_settings"];
  updateLlmSettings: (settings: Partial<Issue["llm_settings"]>) => void;
}

const DataAssetEditor = ({
  workflows,
  getWorkflows,
  clearWorkflows,
  organisationId,
  llm_settings,
  updateLlmSettings,
}: DataAssetEditorProps) => {
  useEffect(() => {
    clearWorkflows();
    getWorkflows(organisationId, {fetch_data_assets: true});

    return () => {
      clearWorkflows();
      getWorkflows(organisationId, {fetch_data_assets: false});
    };
  }, []);

  useEffect(() => {
    const issueSelectedWorkflowId = workflows.find(
      ({id}) => llm_settings.workflow_settings?.selected_workflow === id,
    )?.id;
    const issueSelectedConfigId = issueSelectedWorkflowId
      ? (llm_settings.workflow_settings?.config ?? {})[issueSelectedWorkflowId][
          "workflow_instance_id"
        ]
      : undefined;

    setSelectedDefinitionId(issueSelectedWorkflowId?.toString() ?? "");
    setSelectedConfigId(issueSelectedConfigId?.toString() ?? "");
  }, [workflows.length, llm_settings]);

  const [selectedDefinitionId, setSelectedDefinitionId] = useState<string>("");
  const [selectedConfigId, setSelectedConfigId] = useState<string>("");

  useEffect(() => {
    if (selectedConfigId === "") {
      return;
    }

    const updatedLlmSettings = {
      ...llm_settings,
      workflow_settings: {
        ...llm_settings.workflow_settings,
        config: {
          ...llm_settings.workflow_settings?.config,
          [selectedDefinitionId]: {
            workflow_instance_id: parseInt(selectedConfigId, 10),
          },
        },
        selected_workflow: parseInt(selectedDefinitionId, 10),
      },
    };

    updateLlmSettings(updatedLlmSettings);
  }, [selectedConfigId]);

  const filteredDefinitions = useMemo(() => {
    const defintionsWithAvailableConfigs = workflows.filter(
      definition =>
        definition.workflow_instances?.filter(
          config => !config.is_archived && config.is_public,
        ).length > 0,
    );
    return defintionsWithAvailableConfigs.map(definition => ({
      ...definition,
      workflow_instances:
        definition.workflow_instances?.filter(
          config => !config.is_archived && config.is_public,
        ) || [],
    }));
  }, [workflows]);

  const selectedDefinition = filteredDefinitions.find(
    d => d.id === Number(selectedDefinitionId),
  );

  const configurations = selectedDefinition?.workflow_instances || [];

  const handleDefinitionChange = (definitionId: Definition["id"]) => {
    setSelectedDefinitionId(definitionId.toString());
    setSelectedConfigId("");
  };

  const handleConfigChange = configId => {
    setSelectedConfigId(configId);
  };

  return (
    <div
      style={{
        padding: "0em 2em 1em 2em",
        display: "flex",
        flexDirection: "column",
        gap: "1em",
      }}
    >
      <Paper>
        <div
          className={"app-toolbar"}
          style={{
            paddingLeft: "2rem",
            fontSize: "17px",
            fontWeight: 500,
            justifyContent: "flex-start",
          }}
        >
          Data Asset
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1em",
            padding: "2rem",
          }}
        >
          <h4>Definition</h4>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "1em",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "1em",
                alignItems: "center",
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              <DefinitionSelector
                definitions={filteredDefinitions}
                selectedDefinitionId={parseInt(selectedDefinitionId, 10)}
                handleDefinitionChange={handleDefinitionChange}
                formControlStyles={styles.formControl}
                fullWidth
              />
            </div>
            <div>
              <Link
                to={`/organisation/${organisationId}/data-assets/definition/${selectedDefinitionId}`}
              >
                <IconButton
                  size="small"
                  style={{padding: 0}}
                  aria-label="link to definition"
                >
                  <LinkIcon />
                </IconButton>
              </Link>
            </div>
          </div>
          {selectedDefinitionId && (
            <>
              <h4>Configuration</h4>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "1em",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "1em",
                    alignItems: "center",
                    flexWrap: "wrap",
                    width: "100%",
                  }}
                >
                  <ConfigurationSelector
                    selectedConfigId={selectedConfigId}
                    configurations={configurations.filter(
                      config => config.is_public,
                    )}
                    selectedDefinition={selectedDefinition}
                    handleConfigChange={handleConfigChange}
                    canCreateNew={false}
                    handleCreateNew={() => {}}
                    formControlStyles={{
                      group: styles.formControl,
                      version: styles.formControl,
                    }}
                    fullWidth
                  />
                </div>
                <div>
                  <Link
                    to={`/organisation/${organisationId}/data-assets/definition/${selectedDefinitionId}/config/${selectedConfigId}`}
                  >
                    <IconButton
                      size="small"
                      style={{padding: 0}}
                      aria-label="link to configuration"
                    >
                      <LinkIcon />
                    </IconButton>
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </Paper>
    </div>
  );
};

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    ...bindActionCreators(
      {
        getWorkflows: workflowsFetchAction(requestor),
        clearWorkflows: workflowsClearAction,
      },
      dispatch,
    ),
  };
}

interface WorkflowsState {
  workflows: Workflow[] | typeof UNINITIALISED;
  workflow: Workflow | typeof UNINITIALISED;
}

const mapStateToProps = (state: WorkflowsState) => ({
  workflows: state.workflows === UNINITIALISED ? [] : state.workflows,
});

export default connect(mapStateToProps, mapDispatchToProps)(DataAssetEditor);
